/**
 * Responsibility: Implement state behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type {
  GameState,
  AttributeKey,
  Attribute,
  LogicArtifactReplacements,
  Vitals,
  Skill,
  ProgressClock,
  ScheduledEffectJob,
  ScheduledEffectJobRequest,
} from "@anyquest/shared-core"
import { GameOrigin } from "@anyquest/shared-core"
import { VesselEntity } from './entities'
import { fullVitals as buildFullVitals } from './vitals'
import { formatClock, formatDay, totalMinFromClock } from './temporal'
// ============================================================
// STATE.TS — Game state factory + derived computations
// Plain objects only — no Proxy. State mutations are explicit
// callers dispatch events via the pubsub dispatcher after mutating.
// ============================================================

const DEFAULT_ATTRIBUTES: Record<AttributeKey, Attribute> = {
  STR: { val: 10, desc: 'Power', fullName: 'Strength', aiGuidance: 'Use for direct force, lifting, grappling, and brute-force combat actions.' },
  DEX: { val: 10, desc: 'Precision', fullName: 'Dexterity', aiGuidance: 'Use for fine control, aiming, sleight of hand, lockpicking, and balance.' },
  CON: { val: 10, desc: 'Vigor', fullName: 'Constitution', aiGuidance: 'Use for endurance, pain tolerance, poison resistance, and sustained strain.' },
  INT: { val: 10, desc: 'Logic', fullName: 'Intelligence', aiGuidance: 'Use for analysis, planning, puzzle solving, and technical reasoning.' },
  WIS: { val: 10, desc: 'Insight', fullName: 'Wisdom', aiGuidance: 'Use for judgment, situational interpretation, and reading intent.' },
  CHA: { val: 10, desc: 'Social', fullName: 'Charisma', aiGuidance: 'Use for persuasion, influence, leadership, and social momentum.' },
  PER: { val: 10, desc: 'Acuity', fullName: 'Perception', aiGuidance: 'Use for noticing details, tracking clues, and environmental awareness.' },
  LCK: { val: 10, desc: 'Entropy', fullName: 'Luck', aiGuidance: 'Use when outcomes hinge on fortune, coincidence, or low-control volatility.' },
  WIL: { val: 10, desc: 'Resolve', fullName: 'Willpower', aiGuidance: 'Use for mental resistance, fear control, discipline, and focus under pressure.' },
  FOR: { val: 10, desc: 'Density', fullName: 'Fortitude', aiGuidance: 'Use for stability under impact, heavy blows, and hard-condition resilience.' },
  AGI: { val: 10, desc: 'Speed', fullName: 'Agility', aiGuidance: 'Use for quick movement, dodging, repositioning, and traversal tempo.' },
  PRS: { val: 10, desc: 'Aura', fullName: 'Presence', aiGuidance: 'Use for social weight, intimidation, gravitas, and emotional projection.' },
}

export const HUMAN_AVG_WALKING_SKILL_LEVEL = 10
export const SKILL_LEVEL_DOUBLING_DELTA = 3

export function normalizeSex(value: unknown): 'male' | 'female' | '' {
  const raw = String(value ?? '').trim().toLowerCase()
  if (raw === 'male' || raw === 'm') return 'male'
  if (raw === 'female' || raw === 'f') return 'female'
  return ''
}

export function skillLevelMultiplier(level: number): number {
  const safe = Number.isFinite(level) ? level : HUMAN_AVG_WALKING_SKILL_LEVEL
  return Math.pow(2, (safe - HUMAN_AVG_WALKING_SKILL_LEVEL) / SKILL_LEVEL_DOUBLING_DELTA)
}

const FALLBACK_SKILL_BLUEPRINTS: Array<{
  id: string
  name: string
  desc: string
  levelFrom: (attrs: Record<AttributeKey, Attribute>) => number
}> = [
  {
    id: 'sword_fighting',
    name: 'Sword Fighting',
    desc: 'Close-quarters blade technique and melee timing.',
    levelFrom: attrs => Math.round((attrs.STR.val + attrs.DEX.val + attrs.AGI.val) / 3),
  },
  {
    id: 'swimming',
    name: 'Swimming',
    desc: 'Water movement, breath control, and endurance.',
    levelFrom: attrs => Math.round((attrs.CON.val + attrs.AGI.val + attrs.WIL.val) / 3),
  },
  {
    id: 'survival',
    name: 'Survival',
    desc: 'Tracking, sheltering, and harsh-environment adaptation.',
    levelFrom: attrs => Math.round((attrs.WIS.val + attrs.PER.val + attrs.CON.val) / 3),
  },
  {
    id: 'stealth',
    name: 'Stealth',
    desc: 'Low-profile movement, concealment, and silent approach.',
    levelFrom: attrs => Math.round((attrs.DEX.val + attrs.PER.val + attrs.AGI.val) / 3),
  },
  {
    id: 'persuasion',
    name: 'Persuasion',
    desc: 'Social leverage through rhetoric, presence, and timing.',
    levelFrom: attrs => Math.round((attrs.CHA.val + attrs.PRS.val + attrs.WIL.val) / 3),
  },
  {
    id: 'arcana',
    name: 'Arcana',
    desc: 'Applied mystical understanding and ritual discipline.',
    levelFrom: attrs => Math.round((attrs.INT.val + attrs.WIS.val + attrs.WIL.val) / 3),
  },
]

function clampSkillLevel(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return 10
  return Math.max(1, Math.min(100, Math.round(n)))
}

function toSkillId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function deriveFallbackSkills(attributes: Record<AttributeKey, Attribute>): Skill[] {
  return FALLBACK_SKILL_BLUEPRINTS.map(skill => ({
    id: skill.id,
    name: skill.name,
    desc: skill.desc,
    level: clampSkillLevel(skill.levelFrom(attributes)),
  }))
}

export function normalizeSkills(
  rawSkills: unknown,
  attributes: Record<AttributeKey, Attribute>,
): Skill[] {
  if (!Array.isArray(rawSkills)) return deriveFallbackSkills(attributes)

  const parsed: Skill[] = []
  for (const raw of rawSkills) {
    if (!raw || typeof raw !== 'object') continue
    const candidate = raw as Partial<Skill> & { title?: string }
    const name = String(candidate.name ?? candidate.title ?? '').trim()
    if (!name) continue
    const id = toSkillId(String(candidate.id ?? name))
    if (!id) continue
    const desc = typeof candidate.desc === 'string' ? candidate.desc.trim() : ''
    parsed.push({
      id,
      name,
      level: clampSkillLevel(candidate.level),
      ...(desc ? { desc } : {}),
    })
  }

  if (!parsed.length) return deriveFallbackSkills(attributes)

  const byId = new Map<string, Skill>()
  for (const skill of parsed) {
    const existing = byId.get(skill.id)
    if (!existing || skill.level > existing.level) byId.set(skill.id, skill)
  }
  return [...byId.values()].sort((a, b) => b.level - a.level || a.name.localeCompare(b.name))
}

function toClockId(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function normalizeProgressClocks(rawClocks: unknown): ProgressClock[] {
  if (!Array.isArray(rawClocks)) return []
  const byId = new Map<string, ProgressClock>()
  for (const raw of rawClocks) {
    if (!raw || typeof raw !== 'object') continue
    const candidate = raw as Partial<ProgressClock>
    const id = toClockId(candidate.id ?? candidate.label)
    if (!id) continue
    const label = String(candidate.label ?? id.replace(/_/g, ' ')).trim().slice(0, 80)
    const segmentsRaw = Math.round(Number(candidate.segments))
    const segments = Number.isFinite(segmentsRaw) ? Math.max(2, Math.min(12, segmentsRaw)) : 4
    const filledRaw = Math.round(Number(candidate.filled))
    const filled = Number.isFinite(filledRaw) ? Math.max(0, Math.min(segments, filledRaw)) : 0
    const trigger = typeof candidate.trigger === 'string' ? candidate.trigger.trim().slice(0, 140) : ''
    byId.set(id, {
      id,
      label: label || id.replace(/_/g, ' '),
      segments,
      filled,
      ...(trigger ? { trigger } : {}),
    })
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
}

export function formatProgressClock(clock: ProgressClock): string {
  const trigger = clock.trigger ? ` -> ${clock.trigger}` : ''
  return `${clock.label} ${clock.filled}/${clock.segments}${trigger}`
}

type ProgressClockScope = 'world' | 'room'

type ProgressClockHit = {
  scope: ProgressClockScope
  clock: ProgressClock
  before: number
  after: number
}

export function applyProgressClockUpdates(
  state: GameState,
  updates: Record<string, number> | null | undefined,
): { advanced: ProgressClockHit[]; filled: ProgressClockHit[] } {
  if (!updates || typeof updates !== 'object') return { advanced: [], filled: [] }

  state.world.clocks = normalizeProgressClocks(state.world.clocks)
  state.currentRoom.clocks = normalizeProgressClocks(state.currentRoom.clocks)

  const worldClocks = state.world.clocks
  const roomClocks = state.currentRoom.clocks
  const advanced: ProgressClockHit[] = []
  const filled: ProgressClockHit[] = []

  const resolveTarget = (rawKey: string): { scope: ProgressClockScope; clock: ProgressClock } | null => {
    const key = String(rawKey ?? '').trim()
    if (!key) return null
    const scoped = /^(world|room):(.+)$/.exec(key)
    const scope = scoped?.[1] as ProgressClockScope | undefined
    const id = toClockId(scoped?.[2] ?? key)
    if (!id) return null
    if (scope === 'world') {
      const clock = worldClocks.find(entry => entry.id === id)
      return clock ? { scope, clock } : null
    }
    if (scope === 'room') {
      const clock = roomClocks.find(entry => entry.id === id)
      return clock ? { scope, clock } : null
    }
    const roomClock = roomClocks.find(entry => entry.id === id)
    if (roomClock) return { scope: 'room', clock: roomClock }
    const worldClock = worldClocks.find(entry => entry.id === id)
    return worldClock ? { scope: 'world', clock: worldClock } : null
  }

  for (const [rawKey, rawDelta] of Object.entries(updates)) {
    const delta = Math.round(Number(rawDelta))
    if (!Number.isFinite(delta) || delta === 0) continue
    const target = resolveTarget(rawKey)
    if (!target) continue
    const before = target.clock.filled
    const after = Math.max(0, Math.min(target.clock.segments, before + delta))
    if (after === before) continue
    target.clock.filled = after
    const hit = { ...target, before, after }
    advanced.push(hit)
    if (before < target.clock.segments && after >= target.clock.segments) filled.push(hit)
  }

  const currentNode = state.atlas.nodes[locationKey(state.player.location)]
  if (currentNode) currentNode.clocks = normalizeProgressClocks(state.currentRoom.clocks)

  return { advanced, filled }
}

export function normalizeScheduledEffectJobs(input: unknown): ScheduledEffectJob[] {
  if (!Array.isArray(input)) return []
  const jobs: ScheduledEffectJob[] = []
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue
    const entry = raw as Record<string, unknown>
    const kind = String(entry.kind ?? '').trim()
    const dueMinute = Number(entry.dueMinute)
    if ((kind !== 'trigger_event' && kind !== 'world_log') || !Number.isFinite(dueMinute)) continue
    const id = String(entry.id ?? '').trim() || `${kind}_${Math.max(0, Math.round(dueMinute))}_${jobs.length}`
    const note = String(entry.note ?? '').trim()
    const next: ScheduledEffectJob = {
      id,
      kind,
      dueMinute: Math.max(0, Math.round(dueMinute)),
      ...(note ? { note } : {}),
    }
    if (kind === 'trigger_event' && entry.event && typeof entry.event === 'object') {
      next.event = entry.event as ScheduledEffectJob['event']
    }
    jobs.push(next)
  }
  return jobs
}

export function enqueueScheduledEffectJobs(
  state: GameState,
  jobs: ScheduledEffectJobRequest[] | null | undefined,
): ScheduledEffectJob[] {
  if (!Array.isArray(jobs) || !jobs.length) return []
  const baseMinute = totalMinFromClock(state.world.clock)
  const queue = normalizeScheduledEffectJobs(state.world.scheduledJobs)
  const added: ScheduledEffectJob[] = []
  for (const raw of jobs) {
    if (!raw || typeof raw !== 'object') continue
    const delayMinutes = Number(raw.delayMinutes)
    if (!Number.isFinite(delayMinutes)) continue
    const kind = String(raw.kind ?? '').trim()
    if (kind !== 'trigger_event' && kind !== 'world_log') continue
    const note = String(raw.note ?? '').trim()
    const next: ScheduledEffectJob = {
      id: `${kind}_${baseMinute + Math.max(0, Math.round(delayMinutes))}_${queue.length + added.length}`,
      kind,
      dueMinute: baseMinute + Math.max(0, Math.round(delayMinutes)),
      ...(note ? { note } : {}),
    }
    if (kind === 'trigger_event' && raw.event) next.event = raw.event
    added.push(next)
  }
  if (!added.length) return []
  state.world.scheduledJobs = [...queue, ...added].sort((a, b) => a.dueMinute - b.dueMinute).slice(-32)
  return added
}

export function applyArtifactReplacements(
  state: GameState,
  replacements: LogicArtifactReplacements | null | undefined,
): void {
  if (!replacements) return
  if (Object.prototype.hasOwnProperty.call(replacements, 'activeEvent')) {
    state.activeEvent = replacements.activeEvent == null
      ? null
      : structuredClone(replacements.activeEvent)
  }
}

export function applyDueScheduledEffectJobs(state: GameState): ScheduledEffectJob[] {
  const queue = normalizeScheduledEffectJobs(state.world.scheduledJobs)
  if (!queue.length) {
    state.world.scheduledJobs = []
    return []
  }

  const now = totalMinFromClock(state.world.clock)
  const time = `${formatDay(state.world.clock)}, ${formatClock(state.world.clock)}`
  const keep: ScheduledEffectJob[] = []
  const applied: ScheduledEffectJob[] = []
  state.worldLog = state.worldLog ?? []

  for (const job of queue) {
    if (job.dueMinute > now) {
      keep.push(job)
      continue
    }
    if (job.kind === 'trigger_event') {
      if (state.activeEvent || !job.event) {
        keep.push(job)
        continue
      }
      state.activeEvent = job.event
      if (job.note) state.worldLog.push({ time, text: job.note, type: 'world' })
      applied.push(job)
      continue
    }

    if (job.kind === 'world_log') {
      if (job.note) state.worldLog.push({ time, text: job.note, type: 'world' })
      applied.push(job)
      continue
    }

    keep.push(job)
  }

  state.world.scheduledJobs = keep
  if (state.worldLog.length > 30) state.worldLog = state.worldLog.slice(-30)
  return applied
}

export function defaultGameState(): GameState {
  return {
    meta: {
      name: 'Unnamed Vessel',
      origin: GameOrigin.local,
      timestamp: Date.now(),
    },
    player: {
      identity: '',
      bio: '',
      sex: '',
      location: { x: 0, y: 0 },
      vitals: buildFullVitals(structuredClone(DEFAULT_ATTRIBUTES)),
      attributes: structuredClone(DEFAULT_ATTRIBUTES),
      modifiers: {},
      dormantEffects: [],
      skills: deriveFallbackSkills(DEFAULT_ATTRIBUTES),
      abilities: [],
      traits: [],
      inventory: [],
    },
    companions: [],
    journal: [],
    notebook: [],
    world: {
      seed: '',
      clock: { day: 1, hour: 12, minute: 0 },
      clocks: [],
      scheduledJobs: [],
      narrative: '',
      npcs: [],
      lastTick: 0,
    },
    atlas: { nodes: {}, edges: [] },
    currentRoom: { name: 'Void', desc: '', items: [], npcs: [], dormantFlags: [], clocks: [] },
    history: [],
    worldLog: [],
    activeEvent: null,
    visionUrl: '',
    portraitUrl: '',
    mainQuestCompleted: false,
    gameEnded: false,
  }
}

/**
 * Convert a flat `Record<AttributeKey, number>` (as returned by Genesis AI) into
 * a proper `Record<AttributeKey, Attribute>` with val + desc populated.
 * Attributes missing from `flat` retain their default desc and value.
 */
export function flatStatsToAttributes(
  flat: Record<string, number>,
): Record<AttributeKey, Attribute> {
  const attrs = structuredClone(DEFAULT_ATTRIBUTES)
  for (const [k, v] of Object.entries(flat)) {
    const key = k as AttributeKey
    if (attrs[key] !== undefined) attrs[key].val = v
  }
  return attrs
}

export function normalizeAttributeMetadata(
  attrs: Record<string, Partial<Attribute>> | Record<AttributeKey, Attribute>,
): Record<AttributeKey, Attribute> {
  const merged = structuredClone(DEFAULT_ATTRIBUTES)
  for (const [rawKey, rawAttr] of Object.entries(attrs ?? {})) {
    const key = rawKey as AttributeKey
    if (!merged[key]) continue
    const incoming = rawAttr ?? {}
    if (typeof incoming.val === 'number') merged[key].val = incoming.val
    if (typeof incoming.desc === 'string' && incoming.desc.trim()) merged[key].desc = incoming.desc.trim()
    if (typeof incoming.fullName === 'string' && incoming.fullName.trim()) merged[key].fullName = incoming.fullName.trim()
    if (typeof incoming.aiGuidance === 'string' && incoming.aiGuidance.trim()) merged[key].aiGuidance = incoming.aiGuidance.trim()
  }
  return merged
}

/** Convert a {x,y,z} location to atlas node key "x_y_z". */
export function locationKey(loc: { x: number; y: number; z?: number }): string {
  return `${loc.x}_${loc.y}_${loc.z ?? 0}`
}

/** Parse an atlas node key "x_y_z" → [x, y, z], or null if malformed. */
export function parseLocKey(key: string): [number, number, number] | null {
  if (!key || typeof key !== 'string') return null
  const parts = key.split('_').map(Number)
  if (parts.length < 2) {
    if (key !== '0_0') console.warn(`parseLocKey: Malformed 2D/3D key "${key}"`)
    return null
  }
  const [x, y, z] = parts
  if (isNaN(x) || isNaN(y)) {
    console.warn(`parseLocKey: Non-numeric parts in key "${key}"`)
    return null
  }
  if (parts.length > 2 && isNaN(z)) {
    console.warn(`parseLocKey: Non-numeric Z-part in key "${key}"`)
    return [x, y, 0]
  }
  return [x, y, isNaN(z) ? 0 : z]
}

/** Generate a stable-ish unique ID for a WorldNPC from its name. */
export function generateNpcId(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '_') + '_' + Math.random().toString(36).slice(2, 6)
}

/** Full vitals initialiser at max. */
export function fullVitals(): Vitals {
  return buildFullVitals(structuredClone(DEFAULT_ATTRIBUTES))
}

/** Derived stat: base attribute value + sum of all active modifiers. */
export function calcStat(state: GameState, key: AttributeKey): number {
  return VesselEntity.fromDTO(state.player).calcStat(key)
}

/**
 * Detect if main quest is completed. Override this based on game design:
 * - Most games: check if primary quest (id 'main' or similar) status === 'completed'
 * - Story-driven: check if any major/epic quest reached completion milestone
 * - Sandboxes: main quest optional; can always be considered "complete"
 */
export function isMainQuestCompleted(state: GameState): boolean {
  if (state.mainQuestCompleted) return true
  // Pattern: look for quest with id='main' or type='quest' with status=completed
  const mainQuest = state.journal?.find(q => q.id === 'main' || (q.type === 'quest' && q.status === 'completed'))
  return !!mainQuest
}
